import { useCallback, useMemo, useState } from 'react';
import { UserGroupIcon } from '@heroicons/react/20/solid';

import { ContainerFull } from '../../components/ContainerFull';
import { Heading } from '../../components/Heading';
import { Button } from '../../components/buttons/Button';
import { useTablePrefs } from '../../hooks/useTablePrefs';
import { useUsuariosOnlineDashboard } from '../../hooks/useUsuariosOnlineDashboard';

import { CompactFilterMenu, PeriodCompactFilter } from './CompactFilterMenu';
import { UsuariosOnlineMetricCards } from './UsuariosOnlineMetricCards';
import { UsuariosOnlineLiveTable } from './UsuariosOnlineLiveTable';
import { buildUsuariosOnlineHistoryColumns } from './usuariosOnline.columns';
import {
    DEFAULT_CHART_PERIOD,
    DEFAULT_HISTORY_FILTERS,
    USER_CATEGORY_OPTIONS,
    historyTitleForPeriod,
} from './usuariosOnline.constants';
import { SmartTable } from '../../components/smartTable/SmartTable';

const TABLE_KEY = 'usuarios-online-history';

const DEFAULT_PREFS = {
    query: {
        page: 0,
        pageSize: 10,
        sorting: [{ id: 'connectedAt', desc: true }],
        filters: { ...DEFAULT_HISTORY_FILTERS },
        columnSearch: {},
    },
    view: {
        columnVisibility: {
            userId: false,
            sessionId: false,
        },
        columnOrder: [],
        columnSizing: {},
    },
};

const sortingToApi = (sorting) => {
    if (!Array.isArray(sorting) || sorting.length === 0) {
        return { field: 'connectedAt', dir: 'desc' };
    }
    const [first] = sorting;
    return { field: first.id, dir: first.desc ? 'desc' : 'asc' };
};

const normalizeFilters = (filters = {}) => ({
    period: filters.period || DEFAULT_HISTORY_FILTERS.period,
    userCategory: filters.userCategory || DEFAULT_HISTORY_FILTERS.userCategory,
});

const buildHistoryPayload = (query) => ({
    page: query.page,
    pageSize: query.pageSize,
    filters: normalizeFilters(query.filters),
    columnSearch: query.columnSearch || {},
    sort: sortingToApi(query.sorting),
});

export const UsuariosOnlinePage = () => {
    const [prefs, , patchPrefs, resetPrefs, prefsHydrated] = useTablePrefs(TABLE_KEY, DEFAULT_PREFS);
    const [rolePeriod, setRolePeriod] = useState(DEFAULT_CHART_PERIOD);
    const [devicePeriod, setDevicePeriod] = useState(DEFAULT_CHART_PERIOD);
    const [barPeriod, setBarPeriod] = useState(DEFAULT_CHART_PERIOD);

    const patchQuery = useCallback((patch) => {
        patchPrefs({ query: { ...prefs.query, ...patch } });
    }, [patchPrefs, prefs.query]);

    const patchView = useCallback((patch) => {
        patchPrefs({ view: { ...prefs.view, ...patch } });
    }, [patchPrefs, prefs.view]);

    const handleColumnSearchChange = useCallback((columnId, value) => {
        const current = prefs.query.columnSearch || {};
        const trimmed = (value || '').trim();
        const next = { ...current };
        if (trimmed) {
            if (current[columnId] === trimmed) return;
            next[columnId] = trimmed;
        } else {
            if (!(columnId in current)) return;
            delete next[columnId];
        }
        patchQuery({ page: 0, columnSearch: next });
    }, [patchQuery, prefs.query.columnSearch]);

    const historyQuery = useMemo(() => buildHistoryPayload(prefs.query), [prefs.query]);
    const historyFilters = useMemo(
        () => normalizeFilters(prefs.query.filters),
        [prefs.query.filters],
    );
    const chartPeriods = useMemo(() => ({
        role: rolePeriod,
        device: devicePeriod,
        bar: barPeriod,
    }), [rolePeriod, devicePeriod, barPeriod]);

    const {
        liveMetrics,
        onlineUsers,
        roleMetrics,
        deviceMetrics,
        barMetrics,
        roleLoading,
        deviceLoading,
        barLoading,
        historyRows,
        historyTotal,
        snapshotLoading,
        historyLoading,
        error,
        reload,
    } = useUsuariosOnlineDashboard({
        chartPeriods,
        historyQuery,
        historyReady: prefsHydrated,
    });

    const columns = useMemo(() => buildUsuariosOnlineHistoryColumns(), []);

    const patchHistoryFilter = useCallback((key, value) => {
        patchQuery({
            page: 0,
            filters: {
                ...historyFilters,
                [key]: value,
            },
        });
    }, [historyFilters, patchQuery]);

    return (
        <ContainerFull>
            <Heading
                title='Monitorización de conexiones'
                subtitle='Monitorea las conexiones activas y el histórico de sesiones de la plataforma.'
                center={false}
            />

            {error ? (
                <div className='mt-4 flex flex-col gap-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 md:flex-row md:items-center md:justify-between'>
                    <p>{error}</p>
                    <Button type='button' variant='secondary' onClick={reload}>
                        Reintentar
                    </Button>
                </div>
            ) : null}

            <div className='mt-4'>
                <UsuariosOnlineMetricCards
                    liveMetrics={liveMetrics}
                    liveLoading={snapshotLoading}
                    roleMetrics={roleMetrics}
                    roleLoading={roleLoading}
                    rolePeriod={rolePeriod}
                    onRolePeriodChange={setRolePeriod}
                    deviceMetrics={deviceMetrics}
                    deviceLoading={deviceLoading}
                    devicePeriod={devicePeriod}
                    onDevicePeriodChange={setDevicePeriod}
                    barMetrics={barMetrics}
                    barLoading={barLoading}
                    barPeriod={barPeriod}
                    onBarPeriodChange={setBarPeriod}
                />
            </div>

            <div className='mt-6 grid grid-cols-1 items-stretch gap-4 xl:grid-cols-2'>
                <section className='flex min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm'>
                    <div className='px-4 pb-0 pt-3'>
                        <h3 className='text-lg font-bold text-slate-900'>
                            Conexiones activas en tiempo real
                        </h3>
                        <p className='mt-0.5 text-sm text-slate-500'>
                            Usuarios con sesión activa ahora. Sin filtros.
                        </p>
                    </div>
                    <div className='min-h-0 w-full flex-1 p-4'>
                        <UsuariosOnlineLiveTable
                            users={onlineUsers}
                            loading={snapshotLoading}
                            embedded
                        />
                    </div>
                </section>

                <section className='flex min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm'>
                    <div className='flex flex-col gap-3 px-4 pb-0 pt-3 sm:flex-row sm:items-start sm:justify-between'>
                        <div className='min-w-0'>
                            <h3 className='text-lg font-bold text-slate-900'>
                                {historyTitleForPeriod(historyFilters.period)}
                            </h3>
                            <p className='mt-0.5 text-sm text-slate-500'>
                                Conexiones del periodo y tipo seleccionados.
                            </p>
                        </div>
                        <div className='flex shrink-0 flex-wrap items-center justify-end gap-2'>
                            <PeriodCompactFilter
                                value={historyFilters.period}
                                onChange={(value) => patchHistoryFilter('period', value)}
                                disabled={!prefsHydrated || historyLoading}
                            />
                            <CompactFilterMenu
                                label='Tipo de usuario'
                                icon={UserGroupIcon}
                                options={USER_CATEGORY_OPTIONS}
                                value={historyFilters.userCategory}
                                onChange={(value) => patchHistoryFilter('userCategory', value)}
                                disabled={!prefsHydrated || historyLoading}
                            />
                        </div>
                    </div>
                    <div className='min-h-0 w-full flex-1 p-4'>
                        <SmartTable
                            data={historyRows}
                            columns={columns}
                            totalRows={historyTotal}
                            query={prefs.query}
                            onQueryChange={patchQuery}
                            onColumnSearchChange={handleColumnSearchChange}
                            view={prefs.view}
                            onViewChange={patchView}
                            loading={historyLoading}
                            preferencesReady={prefsHydrated}
                            emptyMessage='No hay registros de conexión para los filtros seleccionados.'
                            pageSizeOptions={[10, 15, 25, 50]}
                            onResetPrefs={resetPrefs}
                            getRowId={(row) => row.id}
                            showColumnSettings={false}
                            enableColumnResizing={false}
                            fillWidth
                            embedded
                        />
                    </div>
                </section>
            </div>
        </ContainerFull>
    );
};
